import { PlausibleEvent } from 'enums/plausible'
import { useEffect } from 'react'
import usePlausible from 'utils/hooks/usePlausible'
import { useInfoProviderContext } from 'utils/providers/InfoProvider/InfoProviderContext'

import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'

import PageLayout from 'layouts/PageLayout'
import { getTitleLocation, MetaTypeNames } from 'pages'

import Markdown from 'components/Markdown/Markdown'
import PageSelector from 'components/PageSelector/PageSelector'
import StructuredData from 'components/StructuredData/StructuredData'
import useIsQVariant from 'utils/hooks/useIsQVariant'

const DynamicRestrictedModal = dynamic(() => import('components/RestrictedModal'))

const WhatIsBinany = ({
  referrer,
  ssrHost,
}: {
  referrer: string
  ssrHost: string
}) => {
  const { t } = useTranslation(['regulation', 'shared', 'contacts', 'main'])
  const { locale } = useRouter()

  const setEvent = usePlausible()

  const isQ = useIsQVariant()

  useEffect(() => {
    setEvent(PlausibleEvent.OpenedWhatis, {
      step: 1,
      referrer,
    })
  }, [])

  const info = useInfoProviderContext()

  if (info?.restricted === true) {
    return <DynamicRestrictedModal />
  }

  return (
    <>
      <StructuredData
        host={ssrHost}
        variant="reduced"
        path="/regulation/whatis"
        pageTitle={t(`seo:${getTitleLocation(ssrHost, 'whatIs', MetaTypeNames.Title)}`)}
      />
      <Head>
        {/* t(`seo:whatIsTitle`) */}
        {/* t(`seo:whatIsTitle24`) */}
        <title>{t(`seo:${getTitleLocation(ssrHost, 'whatIs', MetaTypeNames.Title)}`)}</title>
        {/* t(`seo:whatIsDescription`) */}
        {/* t(`seo:whatIsDescription24`) */}
        <meta
          name="description"
          content={t(`seo:${getTitleLocation(ssrHost, 'whatIs', MetaTypeNames.Description)}`)}
        />

        <meta
          property="og:title"
          content={t(`seo:${getTitleLocation(ssrHost, 'whatIs', MetaTypeNames.Title)}`)}
        />
        <meta
          property="og:description"
          content={t(`seo:${getTitleLocation(ssrHost, 'whatIs', MetaTypeNames.Description)}`)}
        />
        <meta
          property="og:image"
          content={`https://${ssrHost}/images/ogImages/regulationWhatis.png`}
        />
        <meta property="og:site_name" content="Binany - Online Platform for Trading" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://${ssrHost}/regulation/whatis`} />
      </Head>
      <PageLayout
        subtitle={t('main:whatisHereYouWillLearn')}
        title={t('shared:navigationWhatis')}
        breadcrumbs={[
          { title: t('main:binanyTrading'), link: '/' },
          { title: t('shared:navigationWhatis'), link: '/regulation/whatis' },
        ]}
        host={ssrHost}
      >
        <PageSelector
          pages={[
            { title: t('shared:navigationWhatis'), link: '/regulation/whatis', name: 'whatis' },
            {
              title: t('shared:navigationPrivacy'),
              link: isQ ? '/q/regulation/privacy-policy' : '/regulation/privacy-policy',
              name: 'privacy',
            },
            {
              title: t('shared:navigationTerms'),
              link: isQ ? '/q/regulation/terms-and-conditions' : '/regulation/terms-and-conditions',
              name: 'agreement',
            },
          ]}
          pageName="whatis"
          referrer={referrer}
          selectedPage={{
            title: t('shared:navigationWhatis'),
            link: isQ ? '/q/regulation/whatis' : '/regulation/whatis',
            name: 'whatis',
          }}
        />
        <Markdown
          dir={locale === 'ar' ? 'rtl' : 'ltr'}
          components={{ h1: 'h2' }}
          scrollable={false}
        >
          {t('regulation:WhatIsBinany')}
        </Markdown>
      </PageLayout>
    </>
  )
}

export default WhatIsBinany

export async function getServerSideProps({ locale, req }) {
  return {
    props: {
      ssrHost: req.headers.host || '',
      ...(await serverSideTranslations(locale, [
        'regulation',
        'seo',
        'shared',
        'contacts',
        'main',
      ])),
    },
  }
}
